import React, { FC, ReactNode } from 'react';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { times } from 'lodash';
import classNames from 'classnames';

import { LoaderComponents } from 'components';

import { Actions, Column, Data } from './types';
import TableBodyRow from './TableBodyRow';
import useStyles from './styles';

interface Pagination {
  sentinelRef: (node: Element) => void;
  isExhausted: boolean;
  isInitialLoad: boolean;
}

interface Props {
  data: Data;
  columns: Column[];
  actions: Actions;
  pagination: Pagination;
  rowSelection: ReactNode;
}

const ActionTable: FC<Props> = props => {
  const { pagination, columns, actions, data, rowSelection } = props;
  const { sentinelRef, isExhausted, isInitialLoad } = pagination;

  const classes = useStyles();

  function renderLoader(ref: Pagination['sentinelRef'], rows: number) {
    return times(rows, rowIndex => (
      <TableRow ref={ref} key={rowIndex}>
        <TableCell colSpan={columns.length}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <LoaderComponents.Content index={rowIndex} height={20} maxWidth={400} />
            </Grid>
          </Grid>
        </TableCell>
      </TableRow>
    ));
  }

  function renderTableHeaderCell(column: Column, index: number) {
    const { cellProps, label, width } = column;
    const isPenultimate = index === columns.length - 2;
    const penultimateClasses = classNames(classes.tableCell, classes.penultimateCell);

    if (isPenultimate) {
      return (
        <TableCell key={index} className={penultimateClasses} {...cellProps}>
          {label}
        </TableCell>
      );
    }

    return (
      <TableCell
        className={classes.tableCell}
        key={index}
        style={{ maxWidth: width, flexBasis: width }}
        {...cellProps}
      >
        {label}
      </TableCell>
    );
  }

  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow className={classes.tableRow}>{columns.map(renderTableHeaderCell)}</TableRow>
      </TableHead>
      <TableBody>
        {isInitialLoad && renderLoader(null, 8)}
        {rowSelection}
        {data.map((datum, index) => (
          <TableBodyRow
            key={datum.id}
            datum={datum}
            index={index}
            actions={actions}
            columns={columns}
          />
        ))}
        {!isExhausted && renderLoader(sentinelRef, 1)}
      </TableBody>
    </Table>
  );
};

export default ActionTable;
